/**
 * Route fly-through playback engine (docs/plans/2026-07-02-route-flythrough-design.md).
 *
 * Hard constraint #1: this drives a pre-programmed camera path for
 * community orientation only. It is interactive/visual feedback, never
 * LOS truth — the analytical DEM raster (raypath/'s output) is the only
 * source of LOS truth, and nothing here feeds back into it.
 *
 * Deliberately DOM-free and three.js-free (no imports from 'three') so it
 * is unit-testable in plain Node, mirroring camera/groundLock.ts and
 * camera/viewpointJump.ts: playback state lives on the class instance,
 * everything environment-dependent (the polyline, ground sampling, aim
 * targets) is passed in explicitly via the advance() context each call.
 */
import { bearingDegFromForward, yawFromBearingDeg } from '../core/coordinates';
import type { RoutePolyline } from '../core/route';
import { EYE_HEIGHT_M, type GroundSampler } from './groundLock';

export type AutoAimMode = 'array-centre' | 'nearest' | 'road';
/** Camera aim in route mode: the auto-aim targets, plus 'free' where the viewer
 * steers the look direction manually (issue #5). 'road' looks along the route
 * ahead in the direction of travel (issue #21). */
export type AimMode = AutoAimMode | 'free';

/** Order the Aim button cycles the auto-aim targets through. */
const AUTO_AIM_CYCLE: AutoAimMode[] = ['array-centre', 'nearest', 'road'];

/** Free-look pitch clamp (radians): just shy of straight up/down so the view
 * never flips. Mirrors cameraRig's MAX_PITCH for mouse-look. */
export const FREE_MAX_PITCH_RAD = 1.5;

/** How far ahead along the route the 'road' aim looks (metres). A look-ahead
 * (rather than the immediate next vertex) smooths the heading over coarse GPX
 * and reads as "looking down the road". */
export const ROAD_LOOKAHEAD_M = 60;

/** A point the camera can aim at (turbine hub, array centroid, etc). */
export interface RouteTarget {
  x: number;
  /** metres AOD */
  y: number;
  z: number;
  label?: string;
}

export interface RoutePose {
  x: number;
  /** eye elevation, metres AOD */
  y: number;
  z: number;
  /** three.js camera yaw, radians (see core/coordinates.ts forwardFromYaw/yawFromBearingDeg) */
  yaw: number;
  /** camera pitch, radians; positive = looking up */
  pitch: number;
}

export interface RouteAdvanceContext {
  polyline: RoutePolyline;
  sampleGround: GroundSampler;
  arrayCentreTarget: RouteTarget;
  turbineTargets: RouteTarget[];
  eyeHeightM?: number;
  /** base playback speed along the route, metres/second, before speedMultiplier */
  speedMS?: number;
}

const DEFAULT_SPEED_MS = 12;
const MIN_SPEED_MULTIPLIER = 0.1;
const MAX_SPEED_MULTIPLIER = 8;

/**
 * Finds the polyline point at a given along-route distance, linearly
 * interpolating between the surrounding vertices. Clamps to the first/last
 * point outside [0, totalLength] rather than extrapolating.
 */
export function pointAtDistance(polyline: RoutePolyline, distanceM: number): { x: number; z: number } {
  const { points, cumulativeDistances } = polyline;
  if (points.length === 1 || distanceM <= cumulativeDistances[0]) {
    return { x: points[0].x, z: points[0].z };
  }
  const lastIdx = points.length - 1;
  if (distanceM >= cumulativeDistances[lastIdx]) {
    return { x: points[lastIdx].x, z: points[lastIdx].z };
  }
  for (let i = 1; i <= lastIdx; i++) {
    if (distanceM <= cumulativeDistances[i]) {
      const segStart = cumulativeDistances[i - 1];
      const segEnd = cumulativeDistances[i];
      const segLen = segEnd - segStart;
      const t = segLen > 0 ? (distanceM - segStart) / segLen : 0;
      const a = points[i - 1];
      const b = points[i];
      return { x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t };
    }
  }
  // Unreachable given the clamps above, but keeps the return type total.
  return { x: points[lastIdx].x, z: points[lastIdx].z };
}

/** Where on a route the nearest point to some other place sits (issue #183). */
export interface NearestRoutePoint {
  /** Along-route distance in metres of that nearest point, in [0, totalLength]. */
  distanceAlong: number;
  /** Horizontal distance in metres from the query point to that route point. */
  offsetM: number;
}

/**
 * The point on the route closest to a local (x, z), as an along-route distance
 * plus how far off the route the query point actually is (issue #183: a Drive
 * viewpoint selection moves playback to the nearest bit of road, and the status
 * line has to state how far that is from the viewpoint itself).
 *
 * Projects onto each segment and clamps the projection to that segment, so a
 * query point beyond the polyline's start returns 0 and one beyond its end
 * returns totalLength — never an extrapolation off either end, and never a
 * vertex-only snap (an L-shaped route's nearest point is usually the
 * perpendicular foot part-way along a segment, not a vertex). Zero-length
 * segments (a repeated GPX point) contribute their own start rather than a
 * 0/0 NaN, and a single-point polyline is simply distance 0 — the only point
 * there is. Deliberately pure and three.js-free, like pointAtDistance above,
 * so it unit-tests in plain Node.
 *
 * Cost is O(points) — an event-driven call (a viewpoint selection), never
 * per frame.
 */
export function nearestPointOnRoute(polyline: RoutePolyline, x: number, z: number): NearestRoutePoint {
  const { points, cumulativeDistances } = polyline;
  let bestDistanceAlong = 0;
  let bestOffset = Math.hypot(points[0].x - x, points[0].z - z);
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const segLenSq = dx * dx + dz * dz;
    const t = segLenSq > 0 ? Math.min(1, Math.max(0, ((x - a.x) * dx + (z - a.z) * dz) / segLenSq)) : 0;
    const offset = Math.hypot(a.x + dx * t - x, a.z + dz * t - z);
    if (offset < bestOffset) {
      bestOffset = offset;
      const segStart = cumulativeDistances[i - 1];
      bestDistanceAlong = segStart + (cumulativeDistances[i] - segStart) * t;
    }
  }
  return { distanceAlong: bestDistanceAlong, offsetM: bestOffset };
}

/** The nearest target to (x, z) by horizontal distance, or undefined for an empty list. */
function nearestTarget(x: number, z: number, targets: RouteTarget[]): RouteTarget | undefined {
  let best: RouteTarget | undefined;
  let bestDist = Infinity;
  for (const t of targets) {
    const d = Math.hypot(t.x - x, t.z - z);
    if (d < bestDist) {
      bestDist = d;
      best = t;
    }
  }
  return best;
}

export class RoutePlayer {
  distanceAlong = 0;
  speedMultiplier = 1;
  playing = false;
  loop = true;
  aimMode: AimMode = 'array-centre';

  /** The auto-aim target to return to when free-look is released (issue #5).
   * Remembered so the Aim button snaps back to whichever auto mode was active
   * before the viewer took manual control. */
  private autoAimMode: AutoAimMode = 'array-centre';

  /** Manual look direction while aimMode === 'free' (radians). */
  private freeYaw = 0;
  private freePitch = 0;

  /** Yaw/pitch of the last auto-aim pose, so engaging free-look can seed the
   * manual direction from where the camera was already looking (no jump). */
  private lastAutoYaw = 0;
  private lastAutoPitch = 0;

  /**
   * Last successfully ground-sampled eye elevation (metres AOD). Used to
   * hold the camera's height steady when the route runs beyond the tiled
   * AOI (design doc "Off-terrain handling": "the camera floats at its last
   * known eye height" rather than fabricating a ground elevation — hard
   * constraint #6/#7).
   */
  private lastEyeY: number | null = null;

  reset(): void {
    this.distanceAlong = 0;
  }

  togglePlay(): void {
    this.playing = !this.playing;
  }

  setSpeed(multiplier: number): void {
    this.speedMultiplier = Math.min(MAX_SPEED_MULTIPLIER, Math.max(MIN_SPEED_MULTIPLIER, multiplier));
  }

  /** Cycle between the two auto-aim targets (array centre <-> nearest turbine).
   * Leaves free-look; used by the Aim button when not already free. */
  toggleAutoAim(): void {
    const i = AUTO_AIM_CYCLE.indexOf(this.autoAimMode);
    this.autoAimMode = AUTO_AIM_CYCLE[(i + 1) % AUTO_AIM_CYCLE.length];
    this.aimMode = this.autoAimMode;
  }

  /** Snap back from free-look to the remembered auto-aim target (issue #5). */
  restoreAutoAim(): void {
    this.aimMode = this.autoAimMode;
  }

  /** Switch to manual free-look, seeding the look direction from the last
   * auto-aim pose so the view doesn't jump when the viewer takes control. */
  engageFreeLook(): void {
    if (this.aimMode === 'free') return;
    // Remember whichever auto-aim target was active (however it was set), so
    // releasing free-look returns to it.
    this.autoAimMode = this.aimMode;
    this.freeYaw = this.lastAutoYaw;
    this.freePitch = this.lastAutoPitch;
    this.aimMode = 'free';
  }

  /** Rotate the manual look direction by (dYaw, dPitch) radians, engaging
   * free-look on the first nudge. Movement stays route-driven — this only
   * steers the look. Pitch is clamped so the view never flips. */
  nudgeFreeLook(dYaw: number, dPitch: number): void {
    this.engageFreeLook();
    this.freeYaw += dYaw;
    this.freePitch = Math.max(-FREE_MAX_PITCH_RAD, Math.min(FREE_MAX_PITCH_RAD, this.freePitch + dPitch));
  }

  setDistance(distanceM: number, totalLengthM: number): void {
    this.distanceAlong = Math.min(totalLengthM, Math.max(0, distanceM));
  }

  /** Advances playback (if playing) by dt seconds and returns the resulting camera pose. */
  advance(dt: number, ctx: RouteAdvanceContext): RoutePose {
    const totalLength = ctx.polyline.totalLength;
    if (this.playing && totalLength > 0) {
      const speedMS = ctx.speedMS ?? DEFAULT_SPEED_MS;
      this.distanceAlong += dt * this.speedMultiplier * speedMS;
      if (this.distanceAlong >= totalLength) {
        if (this.loop) {
          this.distanceAlong = this.distanceAlong % totalLength;
        } else {
          this.distanceAlong = totalLength;
          this.playing = false;
        }
      }
    }
    return this.poseAt(this.distanceAlong, ctx);
  }

  private poseAt(distanceM: number, ctx: RouteAdvanceContext): RoutePose {
    const { x, z } = pointAtDistance(ctx.polyline, distanceM);
    const eyeHeight = ctx.eyeHeightM ?? EYE_HEIGHT_M;

    const ground = ctx.sampleGround(x, z);
    if (ground !== null) {
      this.lastEyeY = ground + eyeHeight;
    }
    // No ground sampled yet anywhere along the route: fall back to eye
    // height above the local datum rather than fabricating a plausible
    // elevation (hard constraint #7).
    const y = this.lastEyeY ?? eyeHeight;

    // Free-look: the viewer steers the look direction; movement stays on the
    // route (position x/y/z above is unchanged). Deliberately does NOT touch
    // lastAuto*, so releasing free-look resumes the auto-aim cleanly.
    if (this.aimMode === 'free') {
      return { x, y, z, yaw: this.freeYaw, pitch: this.freePitch };
    }

    let dx: number;
    let dz: number;
    let dy: number;
    if (this.aimMode === 'road') {
      // Look along the road ahead (issue #21): aim at a point a fixed distance
      // further along the route, in the direction of travel. On a loop the
      // look-ahead wraps; on a one-shot route it clamps, and if that leaves the
      // ahead-point on top of us (the very end) we fall back to the direction
      // we just came from so the heading never collapses to zero.
      const total = ctx.polyline.totalLength;
      const aheadDist =
        this.loop && total > 0 ? (distanceM + ROAD_LOOKAHEAD_M) % total : Math.min(distanceM + ROAD_LOOKAHEAD_M, total);
      const ahead = pointAtDistance(ctx.polyline, aheadDist);
      dx = ahead.x - x;
      dz = ahead.z - z;
      if (Math.hypot(dx, dz) < 1e-6) {
        const behind = pointAtDistance(ctx.polyline, Math.max(0, distanceM - ROAD_LOOKAHEAD_M));
        dx = x - behind.x;
        dz = z - behind.z;
      }
      // Pitch follows the road grade: aim at eye height above the ahead point's
      // own ground, so a climbing/falling road tilts the view with it rather
      // than into the hillside or the sky. No ground there yet -> stay level.
      const aheadGround = ctx.sampleGround(ahead.x, ahead.z);
      dy = aheadGround !== null ? aheadGround - (y - eyeHeight) : 0;
    } else {
      const target =
        this.aimMode === 'nearest'
          ? (nearestTarget(x, z, ctx.turbineTargets) ?? ctx.arrayCentreTarget)
          : ctx.arrayCentreTarget;
      dx = target.x - x;
      dz = target.z - z;
      dy = target.y - y;
    }

    const horizontal = Math.hypot(dx, dz);
    const bearingDeg = bearingDegFromForward(dx, dz);
    const yaw = yawFromBearingDeg(bearingDeg);
    const pitch = horizontal > 1e-6 ? Math.atan2(dy, horizontal) : 0;

    // Remember where auto-aim was looking, so a later engageFreeLook() can seed
    // the manual direction from here without a visible jump.
    this.lastAutoYaw = yaw;
    this.lastAutoPitch = pitch;

    return { x, y, z, yaw, pitch };
  }
}

/** Centroid of a set of targets (e.g. all turbines), used as the "array centre" aim point. */
export function centroidTarget(targets: RouteTarget[]): RouteTarget {
  if (targets.length === 0) {
    throw new Error('centroidTarget: cannot compute a centroid of zero targets');
  }
  let sx = 0;
  let sy = 0;
  let sz = 0;
  for (const t of targets) {
    sx += t.x;
    sy += t.y;
    sz += t.z;
  }
  return { x: sx / targets.length, y: sy / targets.length, z: sz / targets.length, label: 'array-centre' };
}
